import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import GroupIcon from '@mui/icons-material/Group';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';


// CustomCard overall mini cards with fully responsive text and fluid layout
export const CustomCard = ({ metric, gridProps }) => {
  const IconComp = metric.icon;
  const val = metric.value || 0;
  const lastVal = metric.lastValue || 0;
  const total = metric.total;
  
  const hasComparison = metric.lastValue !== undefined && metric.total !== undefined;
  
  const diff = val - lastVal;
  let percent = "0";
  if (lastVal > 0) {
    percent = Math.abs(Math.round((diff / lastVal) * 100));
  } else if (val > 0) {
    percent = "100";
  }

  // Display value: for Overall Statistics, the big number is total (e.g. 20, 86, 0, 2)
  const displayValue = total !== undefined ? total : val;
  const accentColor = metric.color || '#2563eb';

  return (
    <Grid item xs={12} sm={6} md={3} {...(metric.gridProps || gridProps)}>
      <Link to={metric.link || "#"} style={{ textDecoration: "none", display: "block", height: "100%" }}>
        <Paper
          id={metric.id}
          elevation={0}
          sx={{
            position: 'relative',
            overflow: 'hidden',
            p: hasComparison
              ? { xs: '12px 12px', sm: '13px 14px', lg: '14px 16px' }
              : { xs: '10px 12px', sm: '12px 13px' },
            borderRadius: '14px',
            background: `radial-gradient(circle at 92% 14%, ${accentColor}18 0%, rgba(255, 255, 255, 0.7) 45%, #ffffff 85%)`,
            border: `1px solid ${accentColor}28`,
            boxShadow: `0 4px 16px -4px rgba(15, 23, 42, 0.04), 0 2px 6px -1px ${accentColor}12`,
            transition: 'all 220ms cubic-bezier(0.4, 0, 0.2, 1)',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: hasComparison ? 'space-between' : 'flex-start',
            textAlign: 'left',
            cursor: 'pointer',
            '&:hover': {
              boxShadow: `0 12px 24px -6px ${accentColor}28, 0 4px 10px ${accentColor}12`,
              transform: 'translateY(-3px)',
              borderColor: `${accentColor}55`,
              '& .kpi-icon-badge': {
                transform: 'scale(1.08) rotate(4deg)',
              },
            },
          }}
        >
          {/* Header Row: Glowing Indicator Dot + Label on Left, Floating Gradient Icon on Right */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: hasComparison ? 0.8 : 0.6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, minWidth: 0, pr: 0.5 }}>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: accentColor,
                  boxShadow: `0 0 8px ${accentColor}`,
                  flexShrink: 0,
                }}
              />
              <Tooltip title={metric.label || ''} arrow placement="top">
                <Typography
                  sx={{
                    fontSize: { xs: '0.67rem', sm: '0.71rem' },
                    fontWeight: 800,
                    color: '#475569',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {metric.label}
                </Typography>
              </Tooltip>
            </Box>

            {IconComp && (
              <Box
                className="kpi-icon-badge"
                sx={{
                  width: { xs: 28, sm: 32 },
                  height: { xs: 28, sm: 32 },
                  borderRadius: '9px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: `linear-gradient(135deg, ${accentColor} 0%, ${accentColor}cc 100%)`,
                  color: '#ffffff',
                  boxShadow: `0 4px 10px -2px ${accentColor}40`,
                  flexShrink: 0,
                  transition: 'transform 220ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                }}
              >
                <IconComp sx={{ color: '#ffffff', fontSize: { xs: 15, sm: 17 } }} />
              </Box>
            )}
          </Box>

          {/* Metric Value + Trend Pill */}
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, my: hasComparison ? 0.4 : 0.2 }}>
            <Typography
              sx={{
                fontSize: hasComparison
                  ? { xs: '1.45rem', sm: '1.65rem', lg: '1.75rem' }
                  : { xs: '1.35rem', sm: '1.5rem', lg: '1.6rem' },
                fontWeight: 850,
                color: '#0f172a',
                lineHeight: 1.1,
                letterSpacing: '-0.03em',
              }}
            >
              {displayValue}
            </Typography>

            {hasComparison && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '2px',
                  px: '6px',
                  py: '2px',
                  borderRadius: '9999px',
                  fontSize: '0.62rem',
                  fontWeight: 750,
                  bgcolor: diff > 0 ? '#ecfdf5' : diff < 0 ? '#fef2f2' : '#f8fafc',
                  color: diff > 0 ? '#059669' : diff < 0 ? '#dc2626' : '#64748b',
                  border: diff > 0 ? '1px solid #bbf7d0' : diff < 0 ? '1px solid #fecaca' : '1px solid #e2e8f0',
                  flexShrink: 0,
                }}
              >
                {diff > 0 ? `↑ ${percent}%` : diff < 0 ? `↓ ${percent}%` : `• 0%`}
              </Box>
            )}
          </Box>

          {/* Bottom Micro-Analytics Comparison Pills */}
          {hasComparison ? (
            <Box
              sx={{
                pt: 0.8,
                mt: 0.3,
                borderTop: `1px dashed ${accentColor}24`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 0.6,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 0.7,
                  py: 0.25,
                  borderRadius: '6px',
                  bgcolor: `${accentColor}0f`,
                  border: `1px solid ${accentColor}18`,
                  minWidth: 0,
                }}
              >
                <Typography sx={{ fontSize: '0.63rem', color: '#475569', fontWeight: 650, whiteSpace: 'nowrap' }}>
                  This Month
                </Typography>
                <Typography sx={{ fontSize: '0.7rem', color: accentColor, fontWeight: 850, lineHeight: 1 }}>
                  {val}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 0.7,
                  py: 0.25,
                  borderRadius: '6px',
                  bgcolor: 'rgba(241, 245, 249, 0.85)',
                  border: '1px solid #e2e8f0',
                  minWidth: 0,
                }}
              >
                <Typography sx={{ fontSize: '0.63rem', color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  Last Month
                </Typography>
                <Typography sx={{ fontSize: '0.7rem', color: '#334155', fontWeight: 800, lineHeight: 1 }}>
                  {lastVal}
                </Typography>
              </Box>
            </Box>
          ) : metric.valueLabel ? (
            <Box sx={{ pt: 0.8, borderTop: `1px dashed #e2e8f0` }}>
              <Typography sx={{ fontSize: '0.68rem', color: '#64748b', fontWeight: 600 }}>
                {metric.valueLabel}
              </Typography>
            </Box>
          ) : null}
        </Paper>
      </Link>
    </Grid>
  );
};

// Generic widget container card with clean border and subtle ambient elevation
export const DashboardWidgetCard = ({ children, sx = {}, ...props }) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: '16px 16px', sm: '18px 20px', lg: '20px 22px' },
      borderRadius: '16px',
      bgcolor: '#ffffff',
      border: '1px solid #e2e8f0',
      boxShadow: '0 2px 10px -2px rgba(15, 23, 42, 0.04), 0 1px 3px rgba(15, 23, 42, 0.02)',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      textAlign: 'left',
      transition: 'box-shadow 200ms ease, border-color 200ms ease',
      '&:hover': {
        borderColor: '#cbd5e1',
        boxShadow: '0 6px 22px -4px rgba(15, 23, 42, 0.08)',
      },
      ...sx,
    }}
    {...props}
  >
    {children}
  </Paper>
);

// Shared View All Button Link
export const ViewAllButton = ({ to, label = "View All", sx = {} }) => (
  <Link to={to} style={{ textDecoration: 'none', flexShrink: 0 }}>
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: { xs: 1.15, sm: 1.4 },
        py: { xs: 0.38, sm: 0.45 },
        borderRadius: '8px',
        background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
        color: '#ffffff',
        fontSize: { xs: '0.72rem', sm: '0.74rem' },
        fontWeight: 700,
        boxShadow: '0 2px 8px rgba(37, 99, 235, 0.28)',
        transition: 'all 160ms ease',
        whiteSpace: 'nowrap',
        '&:hover': {
          background: 'linear-gradient(135deg, #1d4ed8, #1e40af)',
          boxShadow: '0 4px 12px rgba(37, 99, 235, 0.4)',
          transform: 'translateY(-1px)',
        },
        ...sx,
      }}
    >
      <span>{label}</span>
      <span style={{ fontSize: '0.85rem' }}>→</span>
    </Box>
  </Link>
);

// Shared Action Pill (used for Approval Request, Job Request badges in section headers)
export const DashboardActionPill = ({
  to,
  label,
  color = '#f59e0b',
  hoverColor = '#d97706',
  onClick,
}) => (
  <Link to={to} style={{ textDecoration: 'none' }}>
    <Box
      component="button"
      type="button"
      sx={{
        borderRadius: '9999px',
        fontWeight: 700,
        fontSize: '0.78rem',
        px: 1.6,
        py: 0.55,
        border: 'none',
        bgcolor: color,
        color: '#ffffff',
        boxShadow: `0 4px 12px ${color}45`,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.8,
        transition: 'all 160ms ease',
        '&:hover': {
          bgcolor: hoverColor,
          transform: 'translateY(-1px)',
        },
      }}
      onClick={onClick}
    >
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#ffffff' }} />
      <span>{label}</span>
    </Box>
  </Link>
);

// Unified Dashboard Section Header (supports title, subtitle, accent bar, and right action/badge)
export const DashboardSectionHeader = ({
  title,
  subtitle,
  accentBar = false,
  action,
  mb = 1.6,
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: { xs: 1.2, sm: 2 },
      mb,
      textAlign: 'left',
      width: '100%',
    }}
  >
    <Box sx={{ flex: 1, minWidth: 0, textAlign: 'left', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {accentBar && (
          <Box
            sx={{
              width: 4,
              height: 16,
              borderRadius: '4px',
              background: 'linear-gradient(180deg, #3b82f6, #6366f1)',
              flexShrink: 0,
            }}
          />
        )}
        <Typography
          sx={{
            fontSize: { xs: '0.92rem', sm: '0.98rem', lg: '1rem' },
            fontWeight: 750,
            color: '#0f172a',
            letterSpacing: '-0.012em',
            textAlign: 'left',
          }}
        >
          {title}
        </Typography>
      </Box>
      {subtitle && (
        <Typography
          sx={{
            fontSize: { xs: '0.72rem', sm: '0.78rem' },
            color: '#64748b',
            mt: 0.25,
            textAlign: 'left',
            lineHeight: 1.35,
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
    {action && (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 1.2,
          flexShrink: 0,
          whiteSpace: 'nowrap',
        }}
      >
        {action}
      </Box>
    )}
  </Box>
);

// Shared Widget Header
export const WidgetHeader = ({ title, action, mb = 1.6, titleSx = {} }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb, gap: 1 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ width: 4, height: 16, borderRadius: '4px', background: 'linear-gradient(180deg, #3b82f6, #6366f1)', flexShrink: 0 }} />
      <Typography
        sx={{
          fontSize: { xs: '0.88rem', sm: '0.92rem', lg: '0.96rem' },
          fontWeight: 750,
          color: '#0f172a',
          letterSpacing: '-0.015em',
          ...titleSx,
        }}
      >
        {title}
      </Typography>
    </Box>
    {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
  </Box>
);

// Shared Timeframe Dropdown Select
export const WidgetTimeframeSelect = ({
  value,
  onChange,
  minWidth = 110,
  options = [
    { value: 'today', label: 'Today' },
    { value: 'monthly', label: 'This Month' },
    { value: 'overall', label: 'Overall' },
  ],
}) => (
  <Select
    value={value}
    onChange={(e) => onChange && onChange(e.target.value)}
    size="small"
    sx={{
      minWidth: { xs: 85, sm: minWidth },
      height: 30,
      fontSize: { xs: '0.72rem', sm: '0.76rem', lg: '0.78rem' },
      bgcolor: '#f8fafc',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      fontWeight: 650,
      color: '#334155',
      boxShadow: 'none',
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
      '&:hover': {
        bgcolor: '#f1f5f9',
      },
      '& .MuiSelect-select': {
        py: '4px',
        px: '10px',
      },
    }}
  >
    {options.map((opt) => (
      <MenuItem
        key={opt.value}
        value={opt.value}
        sx={{
          fontSize: { xs: '0.72rem', sm: '0.78rem' },
          fontWeight: 600,
          '&.Mui-selected': { bgcolor: '#eff6ff', color: '#2563eb' },
        }}
      >
        {opt.label}
      </MenuItem>
    ))}
  </Select>
);

// Work Report Widget (Clean flat list without pop boxes)
export const WorkReportWidget = ({
  performanceData = { topUser: {}, minUser: {} },
  approverPerformanceData = { topUser: {}, minUser: {} },
  timeframe = "today",
  onTimeframeChange,
}) => {
  const reportItems = [
    {
      label: "Top Calibrator",
      userName: performanceData?.topUser?.name || "N/A",
      value: performanceData?.topUser?.count || 0,
      color: "#059669",
      bg: "#ecfdf5",
      borderColor: "#bbf7d0",
      icon: EmojiEventsIcon,
    },
    {
      label: "Lowest Calibrator",
      userName: performanceData?.minUser?.name || "N/A",
      value: performanceData?.minUser?.count || 0,
      color: "#ea580c",
      bg: "#fff7ed",
      borderColor: "#fed7aa",
      icon: GroupIcon,
    },
    {
      label: "Top Approver",
      userName: approverPerformanceData?.topUser?.name || "N/A",
      value: approverPerformanceData?.topUser?.count || 0,
      color: "#2563eb",
      bg: "#eff6ff",
      borderColor: "#bfdbfe",
      icon: WorkspacePremiumIcon,
    },
    {
      label: "Lowest Approver",
      userName: approverPerformanceData?.minUser?.name || "N/A",
      value: approverPerformanceData?.minUser?.count || 0,
      color: "#9333ea",
      bg: "#faf5ff",
      borderColor: "#e9d5ff",
      icon: GroupIcon,
    },
  ];

  return (
    <DashboardWidgetCard>
      <WidgetHeader
        title="Work Report"
        mb={1.5}
        action={<WidgetTimeframeSelect value={timeframe} onChange={onTimeframeChange} />}
      />

      {/* Clean Flat Activity List (No individual pop boxes) */}
      <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, justifyContent: 'space-around', py: 0.5 }}>
        {reportItems.map((item, idx) => {
          const IconComp = item.icon;
          return (
            <Box
              key={idx}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: { xs: 1, sm: 1.1 },
                px: { xs: 0.8, sm: 1 },
                borderRadius: '8px',
                borderBottom: idx !== reportItems.length - 1 ? '1px solid #f1f5f9' : 'none',
                transition: 'background-color 150ms ease',
                '&:hover': {
                  bgcolor: 'rgba(241, 245, 249, 0.65)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.3, minWidth: 0 }}>
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: '10px',
                    background: `linear-gradient(135deg, ${item.color}, ${item.color}dd)`,
                    boxShadow: `0 3px 8px -1px ${item.color}40`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <IconComp sx={{ color: '#ffffff', fontSize: 18 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: '0.68rem',
                      fontWeight: 700,
                      color: '#64748b',
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      lineHeight: 1.2,
                    }}
                  >
                    {item.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: '0.78rem', sm: '0.84rem' },
                      fontWeight: 750,
                      color: '#0f172a',
                      mt: 0.1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: { xs: 120, sm: 140, md: 120, lg: 160 },
                    }}
                  >
                    {item.userName}
                  </Typography>
                </Box>
              </Box>

              {/* Count Pill */}
              <Box
                sx={{
                  px: 1.4,
                  py: 0.35,
                  borderRadius: '9999px',
                  bgcolor: item.bg,
                  border: `1px solid ${item.borderColor}`,
                  flexShrink: 0,
                  ml: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: 42,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.86rem',
                    fontWeight: 800,
                    color: item.color,
                    lineHeight: 1,
                  }}
                >
                  {item.value || 0}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </DashboardWidgetCard>
  );
};

// Recent Inwards Table Widget
export const RecentInwardsWidget = ({ inwards = [] }) => {
  const rows = inwards || [];

  const getStatusStyle = (status) => {
    const s = String(status || '').toLowerCase();
    if (s.includes('complete') || s === '1') {
      return { color: '#059669', bg: '#ecfdf5', border: '#bbf7d0', dot: '#10b981', label: 'Completed' };
    }
    if (s.includes('pending') || s === '0') {
      return { color: '#d97706', bg: '#fffbeb', border: '#fde68a', dot: '#f59e0b', label: 'Pending' };
    }
    return { color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe', dot: '#3b82f6', label: 'In Progress' };
  };

  return (
    <DashboardWidgetCard sx={{ p: { xs: '16px 16px', sm: '18px 20px' } }}>
      {/* Header */}
      <WidgetHeader
        title="Recent Inwards"
        mb={1.5}
        action={<ViewAllButton to="/inward" />}
      />

      {/* Table */}
      <TableContainer
        sx={{
          overflowX: 'auto',
          borderRadius: '12px',
          border: '1px solid #f1f5f9',
        }}
      >
        <Table
          size="small"
          sx={{
            width: '100%',
            tableLayout: 'auto',
            '& th, & td': {
              border: 'none !important',
              borderBottom: '1px solid #f1f5f9 !important',
              py: '8px !important',
              px: '10px !important',
            },
          }}
        >
          <TableHead sx={{ bgcolor: '#f8fafc' }}>
            <TableRow>
              <TableCell sx={{ fontSize: '0.67rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', pl: '12px !important' }}>
                Inward No
              </TableCell>
              <TableCell sx={{ fontSize: '0.67rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Client
              </TableCell>
              <TableCell align="center" sx={{ fontSize: '0.67rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Instruments
              </TableCell>
              <TableCell sx={{ fontSize: '0.67rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Date
              </TableCell>
              <TableCell align="right" sx={{ fontSize: '0.67rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', pr: '12px !important' }}>
                Status
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4, color: '#94a3b8', fontSize: '0.78rem' }}>
                  No recent inwards found
                </TableCell>
              </TableRow>
            ) : (
              rows.slice(0, 5).map((row, index) => {
                const status = getStatusStyle(row.status);
                return (
                  <TableRow
                    key={index}
                    hover
                    sx={{
                      '&:hover': { bgcolor: '#f8fafc' },
                      '&:last-child td': { borderBottom: 'none !important' },
                    }}
                  >
                    <TableCell sx={{ pl: '12px !important', maxWidth: { xs: 85, sm: 110, lg: 130 } }}>
                      <Tooltip title={row.inwardNo} arrow placement="top">
                        <Typography
                          sx={{
                            fontSize: '0.74rem',
                            fontWeight: 700,
                            color: '#2563eb',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {row.inwardNo}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ maxWidth: { xs: 85, sm: 110, lg: 140 } }}>
                      <Tooltip title={row.client} arrow placement="top">
                        <Typography
                          sx={{
                            fontSize: '0.72rem',
                            fontWeight: 500,
                            color: '#475569',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {row.client}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Box
                        sx={{
                          display: 'inline-block',
                          px: 1,
                          py: 0.2,
                          borderRadius: '6px',
                          bgcolor: '#eff6ff',
                          border: '1px solid #bfdbfe',
                          fontSize: '0.72rem',
                          fontWeight: 700,
                          color: '#1d4ed8',
                        }}
                      >
                        {row.totalInstruments}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.70rem', color: '#64748b', whiteSpace: 'nowrap' }}>
                      {row.date}
                    </TableCell>
                    <TableCell align="right" sx={{ pr: '12px !important' }}>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.6,
                          px: 1.1,
                          py: 0.35,
                          borderRadius: '9999px',
                          bgcolor: status.bg,
                          border: `1px solid ${status.border}`,
                          boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
                        }}
                      >
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: status.dot, boxShadow: `0 0 6px ${status.dot}`, flexShrink: 0 }} />
                        <Typography
                          sx={{
                            fontSize: '0.68rem',
                            fontWeight: 700,
                            color: status.color,
                            whiteSpace: 'nowrap',
                            lineHeight: 1,
                          }}
                        >
                          {status.label}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </DashboardWidgetCard>
  );
};

// Upcoming Due Dates Widget with polished table and urgency summary
export const UpcomingDueDatesWidget = ({ dueDates = [] }) => {
  const rows = dueDates || [];

  const getUrgencyConfig = (days) => {
    const d = Number(days);
    if (d <= 5) {
      return { color: '#dc2626', bg: '#fef2f2', border: '#fca5a5', dot: '#ef4444', label: `${d}d left` };
    }
    if (d <= 10) {
      return { color: '#d97706', bg: '#fffbeb', border: '#fde68a', dot: '#f59e0b', label: `${d}d left` };
    }
    return { color: '#059669', bg: '#ecfdf5', border: '#a7f3d0', dot: '#10b981', label: `${d}d left` };
  };

  const criticalCount = rows.filter((r) => Number(r.daysLeft) <= 5).length;
  const warningCount = rows.filter((r) => Number(r.daysLeft) > 5 && Number(r.daysLeft) <= 10).length;
  const safeCount = rows.filter((r) => Number(r.daysLeft) > 10).length;

  const urgencyPills = [
    { count: criticalCount, label: 'Urgent', color: '#dc2626', dot: '#ef4444', bg: '#fef2f2', border: '#fecaca' },
    { count: warningCount, label: 'Approaching', color: '#d97706', dot: '#f59e0b', bg: '#fffbeb', border: '#fde68a' },
    { count: safeCount, label: 'Safe', color: '#059669', dot: '#10b981', bg: '#ecfdf5', border: '#a7f3d0' },
  ].filter((p) => p.count > 0);

  const renderUrgencyPills = () =>
    urgencyPills.map((p) => (
      <Box
        key={p.label}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          px: 0.85,
          py: 0.25,
          borderRadius: '9999px',
          bgcolor: p.bg,
          border: `1px solid ${p.border}`,
          fontSize: '0.67rem',
          fontWeight: 750,
          color: p.color,
          whiteSpace: 'nowrap',
        }}
      >
        <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: p.dot }} />
        <span>{p.count} {p.label}</span>
      </Box>
    ));

  return (
    <DashboardWidgetCard sx={{ p: { xs: '16px 16px', sm: '20px 22px' } }}>
      {/* Header Row */}
      <Box sx={{ mb: 1.8 }}>
        {/* Main Row: Title (+ Desktop Inline Pills) on Left, View All Button on Right */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            width: '100%',
          }}
        >
          {/* Left: Title + Desktop-only Inline Pills */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1, sm: 1.4 },
              minWidth: 0,
            }}
          >
            {/* Title with accent bar */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
              <Box sx={{ width: 4, height: 18, borderRadius: '4px', background: 'linear-gradient(180deg, #f59e0b, #ef4444)', flexShrink: 0 }} />
              <Typography sx={{ fontSize: { xs: '0.94rem', sm: '0.98rem' }, fontWeight: 750, color: '#0f172a', letterSpacing: '-0.015em', whiteSpace: 'nowrap' }}>
                Upcoming Due Dates
              </Typography>
            </Box>

            {/* Desktop-only Inline Urgency Pills (Visible strictly on sm and up) */}
            <Box
              sx={{
                display: { xs: 'none', sm: 'inline-flex' },
                alignItems: 'center',
                gap: 0.6,
                flexWrap: 'nowrap',
                flexShrink: 0,
              }}
            >
              {renderUrgencyPills()}
            </Box>
          </Box>

          {/* Right: View All Button (Always on right on both Mobile and Desktop) */}
          <ViewAllButton to="/scheduler" />
        </Box>

        {/* Mobile-only Urgency Pills (Rendered down below title & button ONLY on xs / phone UI) */}
        {urgencyPills.length > 0 && (
          <Box sx={{ display: { xs: 'flex', sm: 'none' }, alignItems: 'center', gap: 0.6, flexWrap: 'wrap', mt: 1.1 }}>
            {renderUrgencyPills()}
          </Box>
        )}
      </Box>

      {/* Main Content: Empty State vs Polished Table */}
      {rows.length === 0 ? (
        <Box
          sx={{
            py: 4,
            px: 2,
            textAlign: 'center',
            bgcolor: '#f8fafc',
            borderRadius: '12px',
            border: '1px dashed #cbd5e1',
          }}
        >
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: '50%',
              bgcolor: '#ecfdf5',
              color: '#059669',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 1,
              fontSize: '1.25rem',
              fontWeight: 800,
            }}
          >
            ✓
          </Box>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 750, color: '#0f172a' }}>
            All Calibrations Up to Date
          </Typography>
          <Typography sx={{ fontSize: '0.74rem', color: '#64748b', mt: 0.3 }}>
            No instruments are due for calibration within the next 15 days.
          </Typography>
        </Box>
      ) : (
        <TableContainer
          sx={{
            overflowX: 'auto',
            borderRadius: '12px',
            border: '1px solid #f1f5f9',
          }}
        >
          <Table
            size="small"
            sx={{
              width: '100%',
              tableLayout: 'auto',
              '& th, & td': {
                border: 'none !important',
                borderBottom: '1px solid #f1f5f9 !important',
                py: '9px !important',
                px: '10px !important',
              },
            }}
          >
            <TableHead sx={{ bgcolor: '#f8fafc' }}>
              <TableRow>
                <TableCell sx={{ fontSize: '0.67rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', pl: '14px !important', minWidth: 120 }}>
                  Client
                </TableCell>
                <TableCell sx={{ fontSize: '0.67rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', minWidth: 140 }}>
                  Instrument Name
                </TableCell>
                <TableCell sx={{ fontSize: '0.67rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', minWidth: 80 }}>
                  Range
                </TableCell>
                <TableCell sx={{ fontSize: '0.67rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', minWidth: 55 }}>
                  LC
                </TableCell>
                <TableCell sx={{ fontSize: '0.67rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', minWidth: 90 }}>
                  DUC ID
                </TableCell>
                <TableCell sx={{ fontSize: '0.67rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', minWidth: 85 }}>
                  Serial No.
                </TableCell>
                <TableCell sx={{ fontSize: '0.67rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', minWidth: 95 }}>
                  Date of Calib.
                </TableCell>
                <TableCell sx={{ fontSize: '0.67rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', minWidth: 95 }}>
                  Due Date
                </TableCell>
                <TableCell align="right" sx={{ fontSize: '0.67rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', pr: '14px !important', minWidth: 95 }}>
                  Days Left
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.slice(0, 5).map((row, index) => {
                const urgency = getUrgencyConfig(row.daysLeft);
                return (
                  <TableRow
                    key={index}
                    hover
                    sx={{
                      '&:hover': { bgcolor: '#f8fafc' },
                      '&:last-child td': { borderBottom: 'none !important' },
                    }}
                  >
                    <TableCell sx={{ pl: '14px !important', maxWidth: { xs: 100, sm: 130 } }}>
                      <Tooltip title={row.client || ''} arrow placement="top">
                        <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {row.client || '-'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ maxWidth: { xs: 110, sm: 150 } }}>
                      <Tooltip title={row.instrumentName || ''} arrow placement="top">
                        <Typography sx={{ fontSize: '0.73rem', fontWeight: 600, color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {row.instrumentName || '-'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '0.71rem', color: '#475569', whiteSpace: 'nowrap' }}>
                        {row.range || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '0.71rem', color: '#475569', whiteSpace: 'nowrap' }}>
                        {row.lc || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: 'inline-block',
                          px: 0.9,
                          py: 0.2,
                          borderRadius: '6px',
                          bgcolor: '#eff6ff',
                          border: '1px solid #bfdbfe',
                        }}
                      >
                        <Typography sx={{ fontSize: '0.70rem', fontWeight: 700, color: '#1d4ed8', whiteSpace: 'nowrap' }}>
                          {row.ducId || '-'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '0.70rem', color: '#64748b', whiteSpace: 'nowrap' }}>
                        {row.serialNo || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.70rem', color: '#64748b', whiteSpace: 'nowrap' }}>
                      {row.calibDate || '-'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.71rem', fontWeight: 600, color: '#1e293b', whiteSpace: 'nowrap' }}>
                      {row.dueDate || '-'}
                    </TableCell>
                    <TableCell align="right" sx={{ pr: '14px !important' }}>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          px: 1.1,
                          py: 0.35,
                          borderRadius: '9999px',
                          bgcolor: urgency.bg,
                          border: `1px solid ${urgency.border}`,
                          boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
                        }}
                      >
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: urgency.dot, boxShadow: `0 0 5px ${urgency.dot}`, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '0.71rem', fontWeight: 800, color: urgency.color, whiteSpace: 'nowrap', lineHeight: 1 }}>
                          {urgency.label}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </DashboardWidgetCard>
  );
};
